import { useCallback, useEffect, useState } from "react"

import { useNana } from "../../../hooks/useNana"
import { buildHand, restoreHand, refreshCard, rerollMove } from "./selection"
import { ExcludeSets, MOVE_KEYS, MoveCardState, MoveKey } from "./types"

/** Session memory for the Now hand (survives navigate away + click Now again). */
export const NowPageState: {
  cards: MoveCardState[] | null
  exclude: ExcludeSets
  drawn: boolean
} = {
  cards: null,
  exclude: {},
  drawn: false,
}

export function useNowHand() {
  const { model, hub, initialLoadComplete } = useNana()
  const [cards, setCards] = useState<MoveCardState[]>(
    () => NowPageState.cards ?? [],
  )
  const [exclude, setExclude] = useState<ExcludeSets>(
    () => NowPageState.exclude,
  )
  const [drawn, setDrawn] = useState(() => NowPageState.drawn)

  const persist = useCallback(
    (nextCards: MoveCardState[], nextExclude: ExcludeSets, nextDrawn: boolean) => {
      NowPageState.cards = nextCards
      NowPageState.exclude = nextExclude
      NowPageState.drawn = nextDrawn
    },
    [],
  )

  const redrawAll = useCallback(() => {
    if (!model) return
    const nextExclude: ExcludeSets = {}
    const nextCards = buildHand(model, nextExclude)
    setExclude(nextExclude)
    setCards(nextCards)
    setDrawn(true)
    persist(nextCards, nextExclude, true)
  }, [model, persist])

  const reroll = useCallback(
    (move: MoveKey) => {
      if (!model) return
      setCards((prev) => {
        const current = prev.find((c) => c.move === move)
        const { objectId, exclude: nextExclude } = rerollMove(
          move,
          model,
          current?.objectId ?? null,
          exclude,
        )
        setExclude(nextExclude)

        let nextCards: MoveCardState[]
        if (objectId == null) {
          nextCards = prev.filter((c) => c.move !== move)
        } else {
          const merged = new Map(
            prev.filter((c) => c.move !== move).map((c) => [c.move, c]),
          )
          merged.set(move, { move, objectId })
          nextCards = MOVE_KEYS.map((m) => merged.get(m)).filter(
            (c): c is MoveCardState => c != null,
          )
        }

        persist(nextCards, nextExclude, true)
        return nextCards
      })
    },
    [model, exclude, persist],
  )

  // Initial draw or restore once model is loaded
  useEffect(() => {
    if (!initialLoadComplete || !model) return

    if (NowPageState.drawn && NowPageState.cards) {
      const restored = restoreHand(
        NowPageState.cards,
        model,
        NowPageState.exclude,
      )
      setCards(restored)
      setExclude(NowPageState.exclude)
      setDrawn(true)
      persist(restored, NowPageState.exclude, true)
      return
    }

    if (!drawn) {
      redrawAll()
    }
  }, [initialLoadComplete, model, drawn, redrawAll, persist])

  // Refresh cards when underlying objects change / leave pools
  useEffect(() => {
    if (!initialLoadComplete || !drawn) return

    const refresh = () => {
      setCards((prev) => {
        const next: MoveCardState[] = []
        for (const card of prev) {
          const refreshed = refreshCard(card, model, exclude)
          if (refreshed) next.push(refreshed)
        }
        // Keep pipeline order (refreshCard doesn't reorder, but filter might)
        const ordered = MOVE_KEYS.map((m) => next.find((c) => c.move === m)).filter(
          (c): c is MoveCardState => c != null,
        )
        persist(ordered, exclude, true)
        return ordered
      })
    }

    const keys = [
      hub.on("model:source-updated", refresh),
      hub.on("model:source-created", refresh),
      hub.on("model:source-deleted", refresh),
      hub.on("model:fleeting-idea-created", refresh),
      hub.on("model:fleeting-idea-deleted", refresh),
      hub.on("model:idea-created", refresh),
      hub.on("model:ideas-deleted", refresh),
      hub.on("model:idea-updated", refresh),
      hub.on("model:project-created", refresh),
      hub.on("model:project-updated", refresh),
      hub.on("model:project-deleted", refresh),
    ]
    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [hub, model, initialLoadComplete, drawn, exclude, persist])

  return {
    cards,
    initialLoadComplete,
    redrawAll,
    reroll,
  }
}
